import React from 'react';
import {Linking, Platform, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Button from '../../components/themed/Button';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import {logError} from '../../utils/errorLogging';
import type {StaffConflictKind} from '../../utils/staffActivation';

// DIS-1329/1339: terminal screen when staff identity couldn't be confirmed
// (HTTP 200 + active card, but staff benefits need a manual fix). Trigger:
// isStaffActivationBlocked. The CTA opens the support ticket form pre-filled with
// the staff details + a canned description so support gets full context.
// Single source of truth for the canned body — asserted verbatim in the test.
export const STAFF_ACTIVATION_BLOCKED_TICKET_DESCRIPTION =
  "My staff rewards activation couldn't be completed because my details couldn't be verified (identity mismatch). Please help resolve this.";

// DIS-1384: store↔LMG variant-conflict flavour of the same terminal screen —
// account already registered as staff under the other program.
export const STAFF_ACTIVATION_BLOCKED_VARIANT_TICKET_DESCRIPTION =
  "My staff rewards activation couldn't be completed because this account is already registered under a different staff program (store vs LMG). Please help resolve this.";

// DIS-1384: fallback flavour for any other/soft skip reason (no_contact,
// identity_unconfirmed, unknown, missing).
export const STAFF_ACTIVATION_BLOCKED_GENERIC_TICKET_DESCRIPTION =
  "My staff rewards activation couldn't be completed. Please help resolve this.";

// DIS-1384: copy bucket per classifyStaffConflict() result. `lead` is the first
// sentence of the body; `ticket` is the canned description sent with the support
// ticket. name_mismatch preserves the original DIS-1329/1339 copy verbatim.
const STAFF_ACTIVATION_BLOCKED_COPY: Record<StaffConflictKind, {lead: string; ticket: string}> = {
  name_mismatch: {
    lead: "The name linked to your mobile number doesn't match what we have on file.",
    ticket: STAFF_ACTIVATION_BLOCKED_TICKET_DESCRIPTION,
  },
  variant_conflict: {
    lead: "This account is already registered as a staff member under a different program. Store and LMG staff benefits can't be combined on one account.",
    ticket: STAFF_ACTIVATION_BLOCKED_VARIANT_TICKET_DESCRIPTION,
  },
  generic: {
    lead: "We couldn't complete your staff registration.",
    ticket: STAFF_ACTIVATION_BLOCKED_GENERIC_TICKET_DESCRIPTION,
  },
};

const StaffActivationBlockedScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED>) => {
    const {rewardsStore} = useStores();
    const conflictKind = route.params?.conflictKind ?? 'name_mismatch';
    const copy = STAFF_ACTIVATION_BLOCKED_COPY[conflictKind];
    const handleContactSupport = async () => {
      const url = `mailto:${config.SUPPORT_EMAIL}`;
      // RNW Linking.openURL routes mailto through window.open(_blank), which
      // leaves a dead about:blank tab. Navigate the current window so the
      // browser hands off to the mail client (matches utils/navigation.ts).
      if (Platform.OS === 'web') {
        window.location.href = url;
        return;
      }
      try {
        await Linking.openURL(url);
      } catch (e) {
        logError(e, 'StaffActivationBlockedScreen.handleContactSupport');
      }
    };

    const handleSubmitTicket = () => {
      // Cross-navigator hop (rewards flow -> ROOT > MAIN) so it resolves on web
      // (URL -> /contact-us/ticket) and mobile alike. Prefill rides as in-memory
      // params — never the URL — so phone/email never leak into the address bar.
      navigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {
          screen: AppRoute.CONTACT_US_SUBMIT_TICKET,
          params: {
            prefill: {
              firstName: rewardsStore.firstName,
              lastName: rewardsStore.lastName,
              email: rewardsStore.emailAddress,
              phoneNumber: rewardsStore.phoneNumber,
              enquiryType: 'StaffMember',
              description: copy.ticket,
            },
          },
        },
      });
    };

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}}>
          <RewardsProgramImage variant="onLight" />

          <View>
            <Text variant="heading1">WE COULDN&apos;T COMPLETE YOUR SIGN-UP</Text>
          </View>
          <Text>
            {copy.lead} Please contact Customer Support at{' '}
            <Text
              testID="staff-activation-support-email"
              accessibilityRole="link"
              accessibilityLabel={`Contact support at ${config.SUPPORT_EMAIL}`}
              style={{textDecorationLine: 'underline'}}
              onPress={handleContactSupport}>
              {config.SUPPORT_EMAIL}
            </Text>{' '}
            to get this resolved before trying again.
          </Text>

          <View style={{gap: 8, marginTop: 40}}>
            <Button title={'Submit A Ticket'} onPress={handleSubmitTicket} />
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default StaffActivationBlockedScreen;
